import { Box, Grid } from "@mui/material";
import CrossButton from "assets/Primary_white.svg";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import Typography from "components/common/Typography";
import UserAvatarList from "components/common/UserAvatarList";
import { sanitize } from "dompurify";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { accessTypes } from "utils/CommonConstants";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { getPlanDetailsRoute } from "utils/StoreUtils";
import { setIsRedirectFromNotification } from "../MyPlanDetails/MyPlanDetails.slice";
import { Notification } from "./Notifications";
import "./item.scss";

interface Props {
  item: Notification;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  closeNotificationsHandler: () => void;
  handleDeleteNotification: any;
}

function Item(props: Props) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { item, setOpen, closeNotificationsHandler, handleDeleteNotification } = props;
  const isRead = item.isRead === "Y";
  const regExpForUser = /\[~~(.*?)~~]/g;
  const regExpForCardTitle = `[$$cardTitle$$]`;
  const regExpForPlanTitle = `[$$planTitle$$]`;
  const notifiedByList = useAppSelector((state) => state.Notifications.data.notifiedByList);
  const user = notifiedByList?.find((u: any) => u.hltId === item.notifiedBy) || {};
  const { isMobileScreen } = useContext(ThemeContext);
  const { firstName, lastName, displayName } = user;
  const isRemovalNotification = item.type === "MEMBER_REMOVAL_ACTION" || item.type === "MEMBER_REMOVAL_PLAN";
  const impersonationDetails = useAppSelector((state) => state.app.impersonationDetails);
  const isImpersonated = impersonationDetails?.isImpersonated;

  const getContent = () => {
    return item.message
      .replace(regExpForUser, `<b>${getUserNameWithConditionalCheck(displayName, firstName, lastName)}</b>`)
      .replace(regExpForCardTitle, `<b>${item.metadata.cardTitle}</b>`)
      .replace(regExpForPlanTitle, `<b>${item.metadata.planTitle}</b>`);
  };

  const handleNotificationClick = () => {
    if (!isRemovalNotification) {
      setOpen(false);
      closeNotificationsHandler();
      const route = getPlanDetailsRoute();
      let url = `${route}/${item.metadata?.planId}/${item.metadata?.sectionId}/${item.metadata?.cardId}`;

      if (item.type === "STAGNANT_PLAN") {
        url = `${route}/${item.metadata?.planId}`;
      }
      if (item.metadata?.sectionId === "null") {
        url = `${route}/${item.metadata?.planId}?parentTab=Comments`;
      } else {
        dispatch(setIsRedirectFromNotification(true));
      }

      navigate(url);
    }
  };

  return (
    <Grid
      className="item-avatar"
      container
      item
      onClick={handleNotificationClick}
      sx={{
        ".dot": {
          height: "8px",
          width: "8px",
          backgroundColor: "red",
          borderRadius: "50%",
          position: "relative",
          left: "30px",
          top: "1px",
          zIndex: "2",
          border: `1px solid ${tokens.Border.General.Inverse}`
        },
        border: `${isRead ? "1px" : "3px"} solid ${tokens.Border.General.Tertiary}`,
        width: isMobileScreen ? "auto" : "303px",
        height: "117px",
        borderRadius: "12px",
        padding: "10px 10px 16px 16px",
        marginY: "8px",
        cursor: isRemovalNotification ? "inherit" : "pointer"
      }}
    >
      <Grid item xs={3}>
        {!isRead && <Box className="dot"></Box>}
        <UserAvatarList
          sx={{ justifyContent: "flex-end", marginLeft: "6px", marginTop: isRead ? "2px" : "-5px" }}
          variant="colored"
          size="regular"
          users={[user]}
          max={1}
        />
      </Grid>
      <Grid item xs={8}>
        <Typography
          sx={{
            overflow: "hidden",
            minHeight: "63px",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "3",
            WebkitBoxOrient: "vertical"
          }}
          variant="body2"
          color={tokens.Text.General.PrimaryHeadline}
        >
          <span dangerouslySetInnerHTML={{ __html: sanitize(getContent()) }}></span>
        </Typography>
        <Typography variant="label" color={tokens.Text.General.Secondary}>
          {item.createdTimestamp}
        </Typography>
      </Grid>
      <Grid item xs={1}>
        <Button
          accessType={isImpersonated ? accessTypes.delete : ""}
          iconButton
          height="20"
          icon={CrossButton}
          sx={{ paddingTop: "0" }}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteNotification(item.id);
          }}
        />
      </Grid>
    </Grid>
  );
}

export default Item;
